import React from 'react';
import { ChevronRight, PackageSearch } from 'lucide-react';
import { StatusBadge } from '../../../components/deliveries/StatusBadge';
import { formatDateTime } from '../../../features/orderDetails/orderUtils';
import { currentStatusOf } from '../../../features/tracking/trackingUtils';

/**
 * What a tracking search found: up to five orders, newest first, each one a way
 * into its own detail.
 *
 * Each row shows the shipment's status — the same `currentStatusOf` the detail
 * uses, so the row and the page it opens say the same thing. It used to read
 * `mixed_status`, which is the *order's* status while the order is in
 * preparation: in development that disagreed with the shipment on every single
 * order.
 *
 * @param {object} props
 * @param {Array<object>} props.results
 * @param {(orderId: number) => void} props.onSelect
 */
export const TrackingResults = ({ results, onSelect }) => (
  <section data-testid="tracking-results" className="mt-8">
    <h2 className="text-sm font-bold text-text-secondary uppercase tracking-wide mb-3 flex items-center gap-2">
      <PackageSearch size={16} />
      {results.length === 1 ? 'Encontramos tu pedido' : `Encontramos ${results.length} pedidos`}
    </h2>

    <ul className="space-y-3">
      {results.map((order) => {
        const current = currentStatusOf(order);

        return (
          <li key={order.id}>
            <button
              type="button"
              data-testid="tracking-result-row"
              onClick={() => onSelect(order.id)}
              className="w-full text-left bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-border p-5 flex items-center gap-4 hover:border-ninja-green transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="font-bold text-ninja-dark dark:text-white truncate">
                  Pedido #{order.id}
                </p>
                {order.reference_id && (
                  <p className="text-sm text-text-secondary truncate">
                    Referencia {order.reference_id}
                  </p>
                )}
                <p className="text-xs text-text-muted mt-1">
                  Comprado el {formatDateTime(order.created_at)}
                </p>
              </div>

              <div className="shrink-0 flex items-center gap-3">
                {current && (
                  <StatusBadge
                    status={current.status}
                    substatus={current.substatus}
                    testId="tracking-result-status"
                  />
                )}
                <ChevronRight size={18} className="text-text-muted" />
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  </section>
);
